import React, { useState, useRef, useEffect } from 'react';
import './ChatBot.css';
import EnhancedSuggestions from './EnhancedSuggestions';

const suggestedQuestions = [
  "How should I handle URL collisions?",
  "What database is best for this?",
  "How to generate unique short codes?",
  "What's the read:write ratio here?",
  "How to implement caching?",
];

const mockResponses = {
  default: "That's a great question! When designing a URL shortener, consider the trade-offs between different approaches. Think about consistency, availability, and partition tolerance (CAP theorem). What specific aspect would you like to explore?",
  collision: "URL collisions can be handled several ways:\n\n1. **Base62 encoding** - Use a counter + base62 encoding\n2. **Hash + retry** - Generate hash, check DB, retry if collision\n3. **Pre-generated keys** - Use a key generation service (KGS)\n\nThe KGS approach is recommended for high scale as it's faster and avoids race conditions.",
  database: "For a URL shortener at scale:\n\n• **Reads**: Use distributed NoSQL (Cassandra, DynamoDB) for horizontal scaling\n• **Cache**: Redis/Memcached for hot URLs (most accessed)\n• **Writes**: Consider write-through cache pattern\n\nNoSQL is often preferred for the simple key-value access pattern here.",
  cache: "Caching strategy for URL shortener:\n\n1. **Cache hot URLs** - 80% of traffic goes to 20% of URLs (Pareto principle)\n2. **Use LRU eviction** - Least Recently Used policy\n3. **Set TTL** - Cache entries with appropriate TTL\n4. **Read-through cache** - Check cache first, then DB\n\nRedis works great for this use case!",
};

function ChatBot({ evaluation, evaluationLoading = false, evaluationError = null, enableChatbot = false }) {
  const [activeTab, setActiveTab] = useState((evaluation || !enableChatbot) ? 'suggestions' : 'chatbot');
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'bot',
      content: "👋 Hi! I'm your System Design Assistant. Ask me anything about designing the URL shortener - architecture, database choices, scaling strategies, and more!",
      timestamp: new Date(),
    }
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  // Switch to suggestions tab when evaluation comes in
  useEffect(() => {
    if (evaluation) {
      setActiveTab('suggestions');
    }
  }, [evaluation]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const getResponse = (question) => {
    const q = question.toLowerCase();
    if (q.includes('collision')) return mockResponses.collision;
    if (q.includes('database') || q.includes('db')) return mockResponses.database;
    if (q.includes('cache') || q.includes('caching')) return mockResponses.cache;
    return mockResponses.default;
  };

  const handleSend = async (text = input) => {
    if (!text.trim()) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: text,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);

    // Simulate API delay
    setTimeout(() => {
      const botMessage = {
        id: Date.now() + 1,
        type: 'bot',
        content: getResponse(text),
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, botMessage]);
      setIsTyping(false);
    }, 1000 + Math.random() * 1000);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const renderSuggestionsTab = () => {
    if (!evaluation) {
      return (
        <div className="no-evaluation">

          <h3>No Evaluation Yet</h3>
          <p>Submit your design to get AI-powered feedback and suggestions.</p>
        </div>
      );
    }

    return (
      <div className="evaluation-panel">
        {/* Score Section */}
        <div className="eval-score-section">
          <div className="eval-total-score">
            <span className="eval-score-value">{evaluation.score?.total || 0}</span>
            <span className="eval-score-max">/100</span>
          </div>
          <div className="eval-score-breakdown">
            <div className="eval-score-item">
              <span className="eval-score-label">Components</span>
              <div className="eval-score-bar">
                <div className="eval-score-fill" style={{ width: `${(evaluation.score?.breakdown?.components || 0) * 4}%` }}></div>
              </div>
              <span className="eval-score-num">{evaluation.score?.breakdown?.components || 0}/25</span>
            </div>
            <div className="eval-score-item">
              <span className="eval-score-label">APIs</span>
              <div className="eval-score-bar">
                <div className="eval-score-fill" style={{ width: `${(evaluation.score?.breakdown?.apis || 0) * 4}%` }}></div>
              </div>
              <span className="eval-score-num">{evaluation.score?.breakdown?.apis || 0}/25</span>
            </div>
            <div className="eval-score-item">
              <span className="eval-score-label">Algorithms</span>
              <div className="eval-score-bar">
                <div className="eval-score-fill" style={{ width: `${(evaluation.score?.breakdown?.algorithms || 0) * 4}%` }}></div>
              </div>
              <span className="eval-score-num">{evaluation.score?.breakdown?.algorithms || 0}/25</span>
            </div>
            <div className="eval-score-item">
              <span className="eval-score-label">Tradeoffs</span>
              <div className="eval-score-bar">
                <div className="eval-score-fill" style={{ width: `${(evaluation.score?.breakdown?.tradeoffs || 0) * 4}%` }}></div>
              </div>
              <span className="eval-score-num">{evaluation.score?.breakdown?.tradeoffs || 0}/25</span>
            </div>
          </div>
        </div>


        {/* Strengths */}
        {evaluation.strengths?.length > 0 && (
          <div className="eval-section eval-strengths">
            <h3>✅ What You Did Well</h3>
            <ul>
              {evaluation.strengths.map((s, i) => (
                <li key={i}>{s}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Gaps - Questions to think about */}
        {evaluation.gaps?.length > 0 && (
          <div className="eval-section eval-gaps">
            <h3>🤔 Think About This...</h3>
            {evaluation.gaps.map((gap, i) => (
              <div key={i} className={`eval-gap-item priority-${gap.priority}`}>
                <div className="eval-gap-header">
                  <span className={`eval-priority-badge ${gap.priority}`}>
                    {gap.priority}
                  </span>
                </div>
                <p className="eval-gap-question">{gap.question}</p>
                {gap.hint && (
                  <p className="eval-gap-hint">💡 Hint: {gap.hint}</p>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Next Steps */}
        {evaluation.next_steps?.length > 0 && (
          <div className="eval-section eval-next-steps">
            <h3>📋 Suggested Next Steps</h3>
            <ol>
              {evaluation.next_steps.map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ol>
          </div>
        )}
      </div>
    );
  };

  const renderChatbotTab = () => {
    return (
      <>
        <div className="chat-messages">
          {messages.map((message) => (
            <div key={message.id} className={`message ${message.type}`}>
              {message.type === 'bot' && (
                <div className="message-avatar">🤖</div>
              )}
              <div className="message-content">
                <p>{message.content}</p>
                <span className="message-time">
                  {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            </div>
          ))}

          {isTyping && (
            <div className="message bot">
              <div className="message-avatar">🤖</div>
              <div className="message-content typing">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        <div className="suggested-questions">
          <div className="suggestions-label">Quick questions:</div>
          <div className="suggestions-list">
            {suggestedQuestions.map((question, index) => (
              <button
                key={index}
                className="suggestion-chip"
                onClick={() => handleSend(question)}
              >
                {question}
              </button>
            ))}
          </div>
        </div>

        <div className="chat-input-container">
          <textarea
            className="chat-input"
            placeholder="Ask about system design..."
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            rows={1}
          />
          <button
            className="send-btn"
            onClick={() => handleSend()}
            disabled={!input.trim() || isTyping}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13" />
            </svg>
          </button>
        </div>
      </>
    );
  };

  return (
    <div className="right-panel-content">
      <EnhancedSuggestions
        evaluation={evaluation}
        loading={evaluationLoading}
        error={evaluationError}
      />
    </div>
  );
}

export default ChatBot;
